import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Input, Button, Typography, theme, Space, Select, Divider, Modal, Avatar, Skeleton } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faPlus, faUser, faDownload, faInfoCircle, faFileText } from '@fortawesome/free-solid-svg-icons'
import useClinicalNotesTemplates from '../../../../hooks/useClinicalNotesTemplates'
import ClinicalNotesTemplates from '../../../ClinicalNotesTemplates'

const { Text, Title } = Typography

const GenAITemplateSelector = ({ onSelectTemplate, onCancel, query, onQueryChange }) => {
  const { token } = theme.useToken()
  const inputRef = useRef(null)
  const [isFocused, setIsFocused] = useState(false)
  const [showTemplatesModal, setShowTemplatesModal] = useState(false)
  const [templateModalMode, setTemplateModalMode] = useState('browse')

  // Use the clinical notes templates hook
  const { loading, templates, handleFetchTemplates } = useClinicalNotesTemplates({
    setValue: () => {}, // Not needed for template selection
    setModalOpen: () => {}, // Not needed for template selection
    debouncedNoteCapture: () => {}, // Not needed for template selection
    setUpdatingNotes: () => {}, // Not needed for template selection
    noteId: null, // Not needed for template selection
    initialMode: 'browse' // Use browse mode for template listing
  })

  // Restore focus if the input was focused before re-render
  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [query, isFocused])

  // Fetch templates on mount
  useEffect(() => {
    handleFetchTemplates()
  }, [handleFetchTemplates])

  const handleInputChange = useCallback(
    (e) => {
      onQueryChange(e.target.value)
    },
    [onQueryChange]
  )

  const handleInputFocus = () => {
    setIsFocused(true)
  }

  const handleInputBlur = () => {
    setIsFocused(false)
  }

  const presets = [
    { key: 'dictation', name: 'Dictation', icon: faUser },
    { key: 'referral', name: 'Referral letter', icon: faDownload },
    { key: 'patient-handout', name: 'Patient handout', icon: faFileText },
    { key: 'mental-health', name: 'Mental health note', icon: faUser },
    { key: 'skin-check', name: 'Skin check note', icon: faUser },
    { key: 'gp-mptca', name: 'GP MP/TCA', icon: faUser },
    { key: 'mental-health-plan', name: 'Mental Health Care Plan', icon: faDownload },
    { key: 'physio', name: 'Physio note', icon: faUser },
    { key: 'ot', name: 'OT note', icon: faUser },
    { key: 'psychotherapy', name: 'Psychotherapy note', icon: faUser },
    { key: 'smart-goals', name: 'SMART goals', icon: faUser }
  ]

  const handleGenerate = () => {
    if (!query?.trim()) return
    onSelectTemplate({ name: query, type: 'custom', query, shouldGenerate: true })
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && query?.trim()) {
      handleGenerate()
    }
  }

  const handlePresetClick = (preset) => {
    // Generate appropriate prompt based on preset type
    let presetQuery = ''

    switch (preset.key) {
      case 'dictation':
        presetQuery = 'Generate a clinical dictation note from the available patient information'
        break
      case 'referral':
        presetQuery = 'Generate a referral letter to a specialist based on the patient information'
        break
      case 'patient-handout':
        presetQuery =
          "Generate a patient education handout based on the patient's condition, treatment plan, and care instructions. Include easy-to-understand explanations, lifestyle recommendations, warning signs to watch for, and follow-up instructions."
        break
      case 'mental-health':
        presetQuery = 'Generate a mental health assessment note'
        break
      case 'skin-check':
        presetQuery = 'Generate a skin check examination note'
        break
      case 'gp-mptca':
        presetQuery = 'Generate a GP Mental Health Treatment Plan (MP/TCA)'
        break
      case 'mental-health-plan':
        presetQuery = 'Generate a Mental Health Care Plan'
        break
      case 'physio':
        presetQuery = 'Generate a physiotherapy assessment note'
        break
      case 'ot':
        presetQuery = 'Generate an occupational therapy note'
        break
      case 'psychotherapy':
        presetQuery = 'Generate a psychotherapy session note'
        break
      case 'smart-goals':
        presetQuery = 'Generate SMART goals for patient treatment plan'
        break
      default:
        presetQuery = `Generate a ${preset.name} document`
    }

    onSelectTemplate({
      ...preset,
      query: presetQuery,
      shouldGenerate: true
    })
  }

  const handleCreateNewTemplate = () => {
    setTemplateModalMode('create')
    setShowTemplatesModal(true)
  }

  const handleViewAllTemplates = () => {
    setTemplateModalMode('browse')
    setShowTemplatesModal(true)
  }

  const handleTemplateSelection = (template) => {
    // Convert clinical notes template to GenAI template format
    const genAITemplate = {
      name: template.name,
      type: 'saved-template',
      query: `Use the template "${template.name}": ${template.content}`,
      shouldGenerate: true,
      originalTemplate: template
    }
    onSelectTemplate(genAITemplate)
  }

  const handleCloseTemplatesModal = () => {
    setShowTemplatesModal(false)
    setTemplateModalMode('browse') // Reset to browse mode
    // Refresh templates when modal closes in case new ones were created
    handleFetchTemplates()
  }

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white'
      }}
    >
      {/* Input Area */}
      <div
        style={{
          padding: '16px',
          borderBottom: `1px solid ${token.colorBorder}`,
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{ position: 'relative' }}>
          <Input
            placeholder='Ask AI to generate something specific from your transcript...'
            value={query}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            ref={inputRef}
            style={{
              height: '48px',
              fontSize: '16px',
              borderRadius: '24px',
              paddingLeft: '20px',
              paddingRight: query?.trim() ? '60px' : '20px',
              border: `2px solid ${token.colorBorder}`,
              backgroundColor: 'white',
              transition: 'all 0.2s ease'
            }}
            suffix={
              query?.trim() ? (
                <div
                  onClick={handleGenerate}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: token.colorPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = token.colorPrimaryHover
                    e.target.style.transform = 'translateY(-50%) scale(1.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = token.colorPrimary
                    e.target.style.transform = 'translateY(-50%) scale(1)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
              ) : null
            }
          />
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {/* Templates Section */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ fontSize: '22px', marginBottom: '24px', fontWeight: '600' }}>
            Templates
          </Title>
        </div>

        {/* My Templates Section */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={5} style={{ fontSize: '16px', marginBottom: '16px', color: token.colorText }}>
            My Templates
          </Title>

          {/* Create New Template Button */}
          <div
            onClick={handleCreateNewTemplate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 0',
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5'
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent'
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: token.colorPrimary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ color: 'white', fontSize: '14px' }} />
            </div>
            <Text style={{ fontSize: '14px', fontWeight: '500' }}>Create new template</Text>
          </div>

          {/* Templates List */}
          {loading ? (
            // Loading skeleton
            <div style={{ marginTop: '16px' }}>
              {[1, 2].map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
                  <Skeleton.Avatar active size={32} shape='square' />
                  <Skeleton.Input active size='small' style={{ width: 120 }} />
                </div>
              ))}
            </div>
          ) : templates.length > 0 ? (
            // Actual templates
            <div style={{ marginTop: '16px' }}>
              {templates.slice(0, 5).map((template) => (
                <div
                  key={template._id}
                  onClick={() => handleTemplateSelection(template)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.closest('div').style.backgroundColor = '#f5f5f5'
                  }}
                  onMouseLeave={(e) => {
                    e.target.closest('div').style.backgroundColor = 'transparent'
                  }}
                >
                  <Avatar
                    style={{
                      backgroundColor: token.colorPrimary,
                      borderRadius: '6px'
                    }}
                    shape='square'
                    size={32}
                  >
                    {template.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Text
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}
                    title={template.name}
                  >
                    {template.name}
                  </Text>
                </div>
              ))}

              {templates.length > 5 && (
                <div
                  onClick={handleViewAllTemplates}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      backgroundColor: token.colorTextSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: 'white', fontSize: '14px' }} />
                  </div>
                  <Text style={{ fontSize: '14px', fontWeight: '500', color: token.colorTextSecondary }}>
                    View all {templates.length} templates
                  </Text>
                </div>
              )}
            </div>
          ) : (
            // No templates message
            <div style={{ marginTop: '16px', textAlign: 'center', color: token.colorTextSecondary }}>
              <Text style={{ fontSize: '14px' }}>No templates created yet</Text>
            </div>
          )}
        </div>

        {/* Presets Section */}
        <div>
          <Title level={5} style={{ fontSize: '16px', marginBottom: '16px', color: token.colorText }}>
            Presets
          </Title>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {presets.map((preset) => (
              <div
                key={preset.key}
                onClick={() => handlePresetClick(preset)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 0',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f5f5f5'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    backgroundColor: token.colorPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={preset.icon} style={{ color: 'white', fontSize: '14px' }} />
                </div>
                <Text style={{ fontSize: '14px', fontWeight: '500' }}>{preset.name}</Text>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Clinical Notes Templates Modal */}
      <Modal
        footer={false}
        maskClosable={false}
        closable={false}
        width={950}
        open={showTemplatesModal}
        okButtonProps={{ style: { display: 'none' } }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={handleCloseTemplatesModal}
        destroyOnClose
      >
        <ClinicalNotesTemplates
          setModalOpen={setShowTemplatesModal}
          setValue={() => {}} // Not needed for GenAI template creation
          debouncedNoteCapture={() => {}} // Not needed for GenAI template creation
          setUpdatingNotes={() => {}} // Not needed for GenAI template creation
          noteId={null} // Not needed for GenAI template creation
          initialMode={templateModalMode} // Dynamic mode based on button clicked
        />
      </Modal>
    </div>
  )
}

export default GenAITemplateSelector
